import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import Swal from "sweetalert2";
import { formatDate, getImageUrl } from "../../core/api";
import { getNotificationStats, getNotifications, getNotificationRecipients, sendNotification } from "../../core/services/notificationService";
import MetricCard from "../../shared/MetricCard";
import PaginationBar from "../../shared/PaginationBar";
import ToggleSwitch from "../../shared/ToggleSwitch";
import AppImage from "../../shared/AppImage";

type AudienceType = "all" | "active" | "custom";

const EMPTY_FORM = {
  title: "",
  body: "",
  type: "offers",
  audience: "active" as AudienceType,
  sendPush: true,
};

const DELIVERY_HINTS = {
  push: "Sends a phone popup and saves the notification inside the app",
  inApp: "Saves inside the app only — no phone popup alert",
};

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All Users", hint: "Send to every registered user" },
  {
    value: "active",
    label: "Active Users",
    hint: "Send only to active accounts",
  },
  {
    value: "custom",
    label: "Custom",
    hint: "Pick specific users from the list",
  },
];

const readImagePreview = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Image preview failed"));
    reader.readAsDataURL(file);
  });

const Notifications = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });

  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      const [listRes, statsRes] = await Promise.all([
        getNotifications(params),
        getNotificationStats(),
      ]);
      setNotifications(listRes.notifications || []);
      setPagination({
        page: listRes.pagination?.page || page,
        limit: listRes.pagination?.limit || pagination.limit,
        totalPages: listRes.pagination?.totalPages || 1,
        total: listRes.pagination?.total || 0,
      });
      setStats({
        ...(statsRes.stats || {}),
        fcmConfigured: statsRes.fcmConfigured,
      });
    } catch (error: any) {
      Swal.fire(
        "Error",
        error.message || "Unable to load notifications",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "500",
      });

      const result = await getNotificationRecipients(params.toString());
      setUsers(result.users || []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load users", "error");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData(1);
  }, [loadData]);

  useEffect(() => {
    if (form.audience === "custom") {
      void loadUsers();
    } else {
      setSelectedUserIds([]);
      setUserSearch("");
    }
  }, [form.audience, loadUsers]);

  const filteredUsers = useMemo(() => {
    const query = userSearch.toLowerCase().trim();

    if (!query) return users;

    return users.filter((user) => {
      const searchable =
        `${user.name || ""} ${user.email || ""} ${user.phone || ""}`.toLowerCase();
      return searchable.includes(query);
    });
  }, [users, userSearch]);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  };

  const selectAllFiltered = () => {
    const ids = filteredUsers.map((user) => user._id);
    setSelectedUserIds((current) => Array.from(new Set([...current, ...ids])));
  };

  const clearSelection = () => {
    setSelectedUserIds([]);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSelectedUserIds([]);
    setUserSearch("");
    setImageFile(null);
    setImagePreview("");
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = event.target;
    const checked =
      event.target instanceof HTMLInputElement ? event.target.checked : false;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Validation Error", "Please upload a valid image file.", "error");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Validation Error", "Image must be under 5MB.", "error");
      event.target.value = "";
      return;
    }

    try {
      const preview = await readImagePreview(file);
      setImageFile(file);
      setImagePreview(preview);
    } catch {
      Swal.fire("Error", "Unable to preview image.", "error");
    }
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.title.trim() || !form.body.trim()) {
      Swal.fire("Validation Error", "Title and message are required.", "error");
      return;
    }

    if (form.audience === "custom" && selectedUserIds.length === 0) {
      Swal.fire(
        "Validation Error",
        "Select at least one user for custom delivery.",
        "error",
      );
      return;
    }

    setSending(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      payload.append("body", form.body.trim());
      payload.append("type", form.type);
      payload.append("audience", form.audience);
      payload.append("sendPush", String(form.sendPush));

      if (form.audience === "custom") {
        payload.append("userIds", JSON.stringify(selectedUserIds));
      }

      if (imageFile) {
        payload.append("image", imageFile);
      }

      const result = await sendNotification(payload);
      await Swal.fire({
        title: "Notification Sent",
        text: result.message || `Sent to ${result.count || 0} user(s)`,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      resetForm();
      await loadData(1);
    } catch (error: any) {
      Swal.fire(
        "Send Failed",
        error.message || "Unable to send notification",
        "error",
      );
    } finally {
      setSending(false);
    }
  };

  const selectedAudience = AUDIENCE_OPTIONS.find(
    (option) => option.value === form.audience,
  );

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Engagement</div>
          <h1 className="page-title">
            Push & <em>Notifications</em>
          </h1>
          <div className="page-sub">
            Send FCM push and in-app notifications to app users.
            {stats.fcmConfigured === false &&
              " FCM is not configured on server yet."}
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard
          metric={{
            label: "Total Sent",
            value: stats.total || 0,
            icon: "🔔",
            tone: "blue",
          }}
        />
        <MetricCard
          metric={{
            label: "Unread",
            value: stats.unread || 0,
            icon: "📬",
            tone: "orange",
          }}
        />
        <MetricCard
          metric={{
            label: "Push Delivered",
            value: stats.pushSent || 0,
            icon: "📲",
            tone: "green",
          }}
        />
        <MetricCard
          metric={{
            label: "Users With FCM",
            value: stats.usersWithTokens || 0,
            icon: "👤",
            tone: "purple",
          }}
        />
      </section>

      <form
        className="card banner-page notification-page"
        onSubmit={handleSend}
        noValidate
      >
        <div className="card-header">
          <div>
            <h2>Send Notification</h2>
            <p>Choose audience, write message and optionally attach an image.</p>
          </div>
        </div>

        <div className="card-body notification-form-body">
          <div className="two-col">
            <div className="banner-field">
              <label>Title *</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                maxLength={120}
                placeholder="Festive Sale Is Live"
                required
              />
              <small>{form.title.length}/120</small>
            </div>

            <div className="banner-field">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option value="offers">Offers</option>
                <option value="orders">Orders</option>
                <option value="support">Support</option>
                <option value="system">System</option>
              </select>
            </div>
          </div>

          <div className="two-col">
            <div className="banner-field notification-message-field">
              <label>Message *</label>
              <textarea
                name="body"
                value={form.body}
                onChange={handleChange}
                maxLength={500}
                rows={5}
                placeholder="Write the notification message for customers..."
                required
              />
              <small>{form.body.length}/500</small>
            </div>

            <ImageUpload
              label="Notification Image (optional)"
              preview={imagePreview}
              onChange={handleImageUpload}
              onClear={() => {
                setImageFile(null);
                setImagePreview("");
              }}
            />
          </div>

          <div className="two-col">
            <div className="banner-field">
              <label>Send To *</label>
              <select
                name="audience"
                value={form.audience}
                onChange={handleChange}
              >
                {AUDIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedAudience && (
                <small className="notification-field-hint">
                  {selectedAudience.hint}
                </small>
              )}
            </div>

            <div className="banner-field">
              <label>Delivery</label>
              <ToggleSwitch
                checked={form.sendPush}
                onChange={(sendPush) =>
                  setForm((current) => ({ ...current, sendPush }))
                }
                checkedLabel="Push + In-app"
                uncheckedLabel="In-app only"
                hint={form.sendPush ? DELIVERY_HINTS.push : DELIVERY_HINTS.inApp}
              />
            </div>
          </div>

          {form.audience === "custom" && (
            <div className="notification-user-panel">
              <div className="notification-user-panel-head">
                <div>
                  <strong>Select Users</strong>
                  <p>
                    {selectedUserIds.length} selected
                    {users.length ? ` · ${users.length} total users` : ""}
                  </p>
                </div>

                <div className="notification-user-panel-actions">
                  <button
                    type="button"
                    className="btn-sm btn-outline"
                    onClick={selectAllFiltered}
                    disabled={usersLoading || filteredUsers.length === 0}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-outline"
                    onClick={clearSelection}
                    disabled={selectedUserIds.length === 0}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="notification-user-search">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  placeholder="Search by name, email or phone"
                />
              </div>

              <div className="notification-user-list">
                {usersLoading ? (
                  <div className="notification-user-empty">
                    Loading users...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="notification-user-empty">No users found.</div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user._id);

                    return (
                      <div
                        key={user._id}
                        className={`notification-user-item ${isSelected ? "selected" : ""}`}
                      >
                        <ToggleSwitch
                          variant="compact"
                          checked={isSelected}
                          onChange={() => toggleUser(user._id)}
                          label={user.name || "User"}
                        />

                        <div className="notification-user-meta">
                          <strong>{user.name || "Unnamed User"}</strong>
                          <small>
                            {user.phone || user.email || "No contact info"}
                          </small>
                        </div>

                        <span
                          className={`status-badge ${user.isActive ? "success" : "danger"}`}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card-footer">
          <button
            type="button"
            className="btns btn-light"
            onClick={resetForm}
            disabled={sending}
          >
            ✖ Clear
          </button>

          <button type="submit" className="btns btn-primary" disabled={sending}>
            {sending ? "Sending..." : "Send Notification"}
          </button>
        </div>
      </form>

      <section className="table-card">
        <div className="table-head">
          <h2>Recent Notifications</h2>
          <p>Latest in-app notifications sent to customers</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>User</th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Push</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    Loading notifications...
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No notifications sent yet.
                  </td>
                </tr>
              ) : (
                notifications.map((item) => (
                  <tr key={item._id}>
                    <td>
                      {item.image ? (
                        <AppImage
                          className="notification-table-thumb"
                          src={getImageUrl(item.image)}
                          alt={item.title || "Notification"}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <span className="table-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="img-name-cell">
                        <div>
                          <strong>{item.user?.name || "User"}</strong>
                          <small className="table-muted">
                            {item.user?.phone || item.user?.email || "-"}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong>{item.title}</strong>
                      <small className="table-muted">{item.body}</small>
                    </td>
                    <td>
                      <span className="status-badge muted">{item.type}</span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${item.isRead ? "success" : "warning"}`}
                      >
                        {item.isRead ? "Read" : "Unread"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${item.pushSent ? "success" : "muted"}`}
                      >
                        {item.pushSent ? "Sent" : "In-app only"}
                      </span>
                    </td>
                    <td>{formatDate(item.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="notifications"
          onPageChange={loadData}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

const ImageUpload = ({
  label,
  preview,
  onChange,
  onClear,
}: {
  label: string;
  preview: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) => (
  <div className="banner-image-field notification-image-field">
    <span>{label}</span>

    <label className="banner-image-upload notification-image-upload">
      {preview ? (
        <AppImage
          src={preview}
          alt={`${label} preview`}
          crossOrigin={preview.startsWith("http") ? "anonymous" : undefined}
        />
      ) : (
        <div className="upload-placeholder">
          <strong>☁️</strong>
          <b>Upload image</b>
          <small>PNG, JPG or WEBP up to 5MB</small>
        </div>
      )}

      <input
        hidden
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onChange}
      />
    </label>

    {preview ? (
      <button type="button" className="btn-sm btn-outline" onClick={onClear}>
        Remove image
      </button>
    ) : null}
  </div>
);

export default Notifications;
